import { LANES } from '@accra/shared';
import type { RunState, TrafficCar } from '../game/RunState';
import { laneX } from './lanes';
import { steerToward } from './steering';
import { nextJunction, signalAt } from './junctions/signals';
import { recoverFromKnock } from './knock';

const FOLLOW_GAP = 6;
const MERGE_LOOKAHEAD = 12;
const MERGE_CLEARANCE = 9;
/** A hooted-at driver squeezes into a tighter gap than they'd normally take. */
const HORN_CLEARANCE = 5.5;
/** Hooted at with no room to move over: they speed up for a while instead. */
export const HORN_HURRY = { seconds: 3.5, factor: 1.6 } as const;
/** Cars start braking for a red light this far out. */
const RED_LOOKAHEAD = 45;
/** On amber, cars closer than this to the line keep going. */
const AMBER_COMMIT = 9;

/** A red (or late amber) light ahead acts like a stopped car at the stop line. */
function lightAhead(car: TrafficCar, time: number): Leader | null {
  const point = nextJunction(car.s);
  const front = car.s + car.kind.length / 2;
  const toLine = point.stopLine - front;
  if (toLine < -0.5 || toLine > RED_LOOKAHEAD) return null;
  const signal = signalAt(point.seq, time);
  if (signal === 'green' || (signal === 'amber' && toLine < AMBER_COMMIT)) return null;
  return { gap: toLine, speed: 0, hoppable: false };
}

interface Leader {
  gap: number;
  speed: number;
  hoppable: boolean;
}

function gapBetween(back: TrafficCar, frontS: number, frontLength: number): number {
  return frontS - back.s - (frontLength + back.kind.length) / 2;
}

/** Closest thing ahead of a car in its lane: another vehicle, a barrier, or the trotro. */
function leaderAhead(car: TrafficCar, state: RunState): Leader | null {
  let best: Leader | null = null;
  for (const other of state.traffic) {
    if (other === car || other.lane !== car.lane || other.s <= car.s) continue;
    const gap = gapBetween(car, other.s, other.kind.length);
    if (!best || gap < best.gap) best = { gap, speed: other.speed, hoppable: other.kind.hoppable };
  }
  const light = lightAhead(car, state.stats.seconds);
  if (light && (!best || light.gap < best.gap)) best = light;
  const { bus } = state;
  if (bus.targetLane === car.lane && bus.s > car.s) {
    const gap = gapBetween(car, bus.s, bus.size.length);
    if (!best || gap < best.gap) best = { gap, speed: bus.speed, hoppable: false };
  }
  return best;
}

function laneIsClear(state: RunState, lane: number, s: number, clearance: number): boolean {
  if (state.bus.targetLane === lane && Math.abs(state.bus.s - s) < clearance * 2) return false;
  return !state.traffic.some((other) => other.lane === lane && Math.abs(other.s - s) < clearance);
}

/** Cars change lane to get round barriers (the view smooths the move). */
export function tryMerge(car: TrafficCar, state: RunState, clearance = MERGE_CLEARANCE): boolean {
  for (const lane of [car.lane - 1, car.lane + 1]) {
    if (lane < 0 || lane >= LANES) continue;
    if (laneIsClear(state, lane, car.s, clearance)) {
      car.lane = lane;
      return true;
    }
  }
  return false;
}

/**
 * A hoot at a car ahead in our lane: it pulls over into the next lane if there's any room,
 * otherwise it puts its foot down and pulls away for a few seconds. Returns true if it moved over.
 */
export function hootAt(car: TrafficCar, state: RunState): boolean {
  if (tryMerge(car, state, HORN_CLEARANCE)) return true;
  car.hurry = HORN_HURRY.seconds;
  return false;
}

/** Moves traffic forward. Cars queue behind slower vehicles (including the trotro) and merge round barriers. */
export function updateTrafficFlow(state: RunState, dt: number): void {
  for (const car of state.traffic) {
    // Crashed into: stand still for a moment (barriers just lie there).
    if (recoverFromKnock(car, dt) || car.kind.hoppable) continue;
    steerToward(car, laneX(car.lane), car.speed, dt);
    const leader = leaderAhead(car, state);
    if (leader?.hoppable && leader.gap < MERGE_LOOKAHEAD && tryMerge(car, state)) {
      car.s += car.speed * dt;
      continue;
    }
    let speed = car.speed * state.effects.trafficSpeed;
    if (car.hurry && car.hurry > 0) {
      car.hurry = Math.max(0, car.hurry - dt);
      speed *= HORN_HURRY.factor;
    }
    if (leader && leader.gap < FOLLOW_GAP) speed = Math.min(speed, leader.speed * Math.max(0, leader.gap / FOLLOW_GAP));
    car.s += speed * dt;
  }
}
